import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Phone, Clock, Star, User, Building2, ChevronLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import SEOHead from '../components/SEOHead';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { SEO_IMAGES, SITE_URL, absoluteUrl } from '../utils/seo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClinicProfilePage() {
  const { id } = useParams();
  const [clinic, setClinic] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchClinicData();
  }, [id]);

  const fetchClinicData = async () => {
    setLoading(true);
    try {
      const [clinicRes, doctorsRes] = await Promise.all([
        axios.get(`${API_URL}/api/public/providers/${id}`),
        axios.get(`${API_URL}/api/public/providers/${id}/doctors`)
      ]);
      setClinic(clinicRes.data);
      setDoctors(doctorsRes.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="container-app py-8">
          <Skeleton className="h-64 rounded-2xl mb-6" />
          <Skeleton className="h-8 w-64 mb-4" />
          <Skeleton className="h-4 w-48" />
        </div>
      </div>
    );
  }

  if (!clinic) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="container-app py-16 text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Clínica não encontrada</h1>
          <Link to="/buscar">
            <Button>Voltar para busca</Button>
          </Link>
        </div>
      </div>
    );
  }

  const clinicTitle = `${clinic.trade_name} | Especialidades e Atendimento | BuscaDoutor`;
  const clinicDescription = `Consulte informações da clínica ${clinic.trade_name}, especialidades disponíveis, localização e dados de atendimento no BuscaDoutor.`;
  const clinicPath = `/clinica/${id}`;
  const clinicImage = SEO_IMAGES.clinics;
  const clinicSchema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalClinic',
    name: clinic.trade_name,
    url: `${SITE_URL}${clinicPath}`,
    image: absoluteUrl(clinicImage),
    ...(clinic.description && { description: clinic.description.slice(0, 500) }),
    ...(clinic.phone && { telephone: clinic.phone }),
    address: {
      '@type': 'PostalAddress',
      ...(clinic.address && { streetAddress: clinic.address }),
      ...(clinic.neighborhood && { addressLocality: `${clinic.neighborhood}, ${clinic.city || ''}`.trim() }),
      ...(clinic.state && { addressRegion: clinic.state }),
      addressCountry: 'BR',
    },
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="clinic-profile-page">
      <SEOHead
        title={clinicTitle}
        description={clinicDescription}
        canonicalUrl={clinicPath}
        imageUrl={clinicImage}
        structuredData={clinicSchema}
      />
      <Navbar />

      <div className="container-app py-8">
        <div className="max-w-4xl mx-auto">
          <Link to="/buscar" className="inline-flex items-center gap-2 text-slate-600 hover:text-primary mb-6">
            <ChevronLeft className="w-4 h-4" />
            Voltar para busca
          </Link>

          {/* Clinic Header */}
          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden mb-6">
            {clinic.photos?.length > 0 && (
              <div className="h-48 bg-slate-100">
                <img src={clinic.photos[0]} alt={clinic.trade_name} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="p-6 md:p-8">
              <div className="flex items-start gap-6">
                <div className="w-20 h-20 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  {clinic.logo ? (
                    <img src={clinic.logo} alt={clinic.trade_name} className="w-full h-full object-cover rounded-xl" />
                  ) : (
                    <Building2 className="w-10 h-10 text-slate-400" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {clinic.is_verified && (
                      <Badge className="bg-emerald-100 text-emerald-700">Verificada</Badge>
                    )}
                  </div>
                  <h1 className="text-2xl font-bold text-slate-900 mb-2">{clinic.trade_name}</h1>
                  <div className="flex items-center gap-2 text-slate-600">
                    <MapPin className="w-4 h-4" />
                    <span>{clinic.address}, {clinic.neighborhood} - {clinic.city}/{clinic.state}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600 mt-2">
                    <Phone className="w-4 h-4" />
                    <span>{clinic.phone}</span>
                  </div>
                  {clinic.rating_avg > 0 && (
                    <div className="flex items-center gap-2 mt-3">
                      <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                      <span className="font-semibold">{clinic.rating_avg.toFixed(1)}</span>
                      <span className="text-slate-500">({clinic.rating_count} avaliações)</span>
                    </div>
                  )}
                </div>
              </div>

              {clinic.description && (
                <p className="text-slate-600 mt-6 border-t border-slate-100 pt-6">
                  {clinic.description}
                </p>
              )}
            </div>
          </div>

          {/* Doctors */}
          <div className="bg-white rounded-2xl border border-slate-100 p-6">
            <h2 className="text-xl font-semibold text-slate-900 mb-6">
              Médicos ({doctors.length})
            </h2>
            {doctors.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">
                {doctors.map((doctor) => (
                  <Link
                    key={doctor.id}
                    to={`/medico/${doctor.id}`}
                    className="flex items-center gap-4 p-4 rounded-xl border border-slate-100 hover:border-emerald-200 transition-colors"
                  >
                    <div className="w-14 h-14 bg-slate-100 rounded-lg flex items-center justify-center">
                      {doctor.photo ? (
                        <img src={doctor.photo} alt={doctor.full_name} className="w-full h-full object-cover rounded-lg" />
                      ) : (
                        <User className="w-7 h-7 text-slate-400" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-900">{doctor.full_name}</h3>
                      <p className="text-sm text-slate-600">{doctor.specialty_name}</p>
                      {doctor.rating_avg > 0 && (
                        <div className="flex items-center gap-1 mt-1">
                          <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                          <span className="text-xs font-medium">{doctor.rating_avg.toFixed(1)}</span>
                        </div>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">Nenhum médico cadastrado</p>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
